import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import USCustomersInfo from '../components/USCustomersInfo';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import ProductLimitationsNotice from '../components/ProductLimitationsNotice';
import SEOHead from '../components/SEOHead';
import JsonLd, { breadcrumbSchema, faqSchema } from '../components/JsonLd';

const FAQPage = () => {
  const { t, language } = useLanguage();
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  }, [hash]);

  // Flatten all FAQ pairs from translations for the FAQPage schema.
  const rawSections = t('faq.sections');
  const allPairs = Array.isArray(rawSections)
    ? rawSections.flatMap((s) => (s.items || []).map((q) => ({ question: q.question, answer: q.answer })))
    : [];

  return (
    <main className="pt-20 sm:pt-24">
      <SEOHead
        routeKey="faq"
        language={language}
        title={language === 'fr' ? 'FAQ — Questions fréquentes | MyGuardPod' : 'FAQ — Frequently Asked Questions | MyGuardPod'}
        description={language === 'fr'
          ? 'Réponses factuelles aux questions les plus fréquentes sur les étuis MyGuardPod : compatibilité, limitations, retour et livraison.'
          : 'Factual answers to the most frequently asked questions about MyGuardPod cases: compatibility, limitations, returns and shipping.'}
      />
      <JsonLd data={breadcrumbSchema([{ name: 'FAQ', routeKey: 'faq' }], language)} />
      {allPairs.length > 0 && <JsonLd data={faqSchema(allPairs)} />}
      {/* Hero */}
      <section className="bg-neutral-50 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1a1a1a] mb-6">
              {t('faq.title')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {t('faq.sections').map((section, sectionIndex) => (
              <motion.div
                key={sectionIndex}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: sectionIndex * 0.1 }}
              >
                <h2 className="font-outfit font-bold text-lg uppercase tracking-widest text-[#E31B23] mb-6">
                  {section.title}
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  {section.items.map((item, itemIndex) => {
                    const globalIndex = `s${sectionIndex}-i${itemIndex}`;
                    return (
                      <AccordionItem
                        key={globalIndex}
                        value={globalIndex}
                        className="bg-neutral-50 rounded-2xl border border-neutral-200 px-6"
                      >
                        <AccordionTrigger
                          className="font-outfit font-bold text-left text-[#1a1a1a] hover:text-[#E31B23] py-5"
                          data-testid={`faq-trigger-${sectionIndex}-${itemIndex}`}
                        >
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className="font-manrope text-neutral-600 pb-5">
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </motion.div>
            ))}
          </div>

          <div className="mt-16">
            <USCustomersInfo />
          </div>

          <div className="mt-16 max-w-4xl mx-auto">
            <ProductLimitationsNotice />
          </div>
        </div>
      </section>
    </main>
  );
};

export default FAQPage;
